// client/src/components/reports/reportModel.js
//
// Pure helpers for the report builder. The server (server/reports/
// definition.js) is the authority and re-validates everything; these only
// keep the UI from offering choices the server would reject.

export const NUMERIC = ['integer', 'number', 'currency', 'percent'];
export const BUCKETS = ['day', 'week', 'month', 'quarter', 'year'];
export const AGG_FNS = ['count', 'count_distinct', 'sum', 'avg', 'min', 'max'];
export const VIZ = ['table', 'bar', 'line', 'kpi'];

const RELATIVE = ['today', 'yesterday', 'last_n_days', 'next_n_days', 'this_week', 'last_week',
  'this_month', 'last_month', 'this_quarter', 'last_quarter', 'this_year', 'year_to_date', 'last_year'];

export const OPS = {
  string: ['contains', 'eq', 'neq', 'starts_with', 'not_contains', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  enum: ['in', 'eq', 'neq', 'not_in', 'is_empty', 'is_not_empty'],
  numeric: ['gte', 'lte', 'eq', 'neq', 'gt', 'lt', 'between', 'is_empty', 'is_not_empty'],
  date: ['last_n_days', ...RELATIVE.filter((o) => o !== 'last_n_days'), 'on', 'before', 'after', 'between', 'is_empty', 'is_not_empty'],
  boolean: ['is_true', 'is_false'],
};

export function opFamily(type) {
  if (type === 'string' || type === 'text') return 'string';
  if (type === 'enum') return 'enum';
  if (NUMERIC.includes(type)) return 'numeric';
  if (type === 'date' || type === 'datetime') return 'date';
  return 'boolean';
}

const NO_VALUE = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false',
  ...RELATIVE.filter((o) => o !== 'last_n_days' && o !== 'next_n_days')]);

/** What kind of input an op needs: none | days | single | pair | list */
export function valueKind(op) {
  if (NO_VALUE.has(op)) return 'none';
  if (op === 'last_n_days' || op === 'next_n_days') return 'days';
  if (op === 'between') return 'pair';
  if (op === 'in' || op === 'not_in') return 'list';
  return 'single';
}

/** A sensible starting value when the op changes. */
export function defaultValue(field, op) {
  const kind = valueKind(op);
  if (kind === 'none') return undefined;
  if (kind === 'days') return 30;
  if (kind === 'list') return field.type === 'enum' ? [field.options[0]] : [];
  if (kind === 'pair') return ['', ''];
  return field.type === 'enum' ? field.options[0] : '';
}

/** Is this filter complete enough to send? */
export function filterReady(f) {
  const kind = valueKind(f.op);
  if (!f.field || !f.op) return false;
  if (kind === 'none') return true;
  // Only an empty box is "not filled in yet". A typed 0 or 9999 is sent so the
  // server refuses it — dropping it would save an all-time report instead.
  if (kind === 'days') return f.value !== '' && f.value !== undefined && f.value !== null;
  if (kind === 'list') return Array.isArray(f.value) && f.value.length > 0;
  if (kind === 'pair') return Array.isArray(f.value) && f.value[0] !== '' && f.value[1] !== '';
  return f.value !== '' && f.value !== undefined && f.value !== null;
}

/** Start a new report on a dataset: a few sensible columns, no filters. */
export function newDefinition(ds) {
  const visible = ds.fields.filter((f) => !f.masked);
  const fields = visible.slice(0, 5).map((f) => f.key);
  const filters = ds.defaultDateField ? [{ field: ds.defaultDateField, op: 'last_n_days', value: 30 }] : [];
  const params = Object.fromEntries((ds.params || []).map((p) => [p.key, p.default]));
  return { dataset: ds.key, params, fields, groupBy: [], aggregates: [], filters, sort: [], limit: null, visualization: { type: 'table' } };
}

export const isSummary = (def) => (def.groupBy && def.groupBy.length > 0) || (def.aggregates && def.aggregates.length > 0);

/** What the server needs: incomplete filters dropped, empty bits removed. */
export function toRequest(def) {
  const out = {
    dataset: def.dataset,
    params: def.params && Object.keys(def.params).length ? def.params : undefined,
    filters: (def.filters || []).filter(filterReady).map((f) => {
      const kind = valueKind(f.op);
      const v = kind === 'none' ? undefined
        : kind === 'days' ? Number(f.value)
          : f.value;
      return v === undefined ? { field: f.field, op: f.op } : { field: f.field, op: f.op, value: v };
    }),
    sort: (def.sort || []).filter((s) => s.field),
    limit: def.limit ? Number(def.limit) : undefined,
    visualization: def.visualization,
  };
  if (isSummary(def)) {
    out.groupBy = def.groupBy.filter((g) => g.field);
    out.aggregates = def.aggregates.filter((a) => a.fn === 'count' || a.field).map((a) => {
      const x = { fn: a.fn };
      if (a.field) x.field = a.field;
      if (a.as) x.as = a.as;
      if (a.label) x.label = a.label;
      return x;
    });
  } else {
    out.fields = def.fields;
  }
  if (out.visualization && out.visualization.type === 'table') out.visualization = { type: 'table' };
  return out;
}

/** Can this be previewed yet? */
export function previewable(def) {
  if (!def || !def.dataset) return false;
  return isSummary(def) ? true : (def.fields || []).length > 0;
}

/** Stored / template definition → builder state (fills the arrays the UI expects). */
export function fromStored(def) {
  return {
    dataset: def.dataset,
    params: def.params || {},
    fields: def.fields || [],
    groupBy: (def.groupBy || []).map((g) => (typeof g === 'string' ? { field: g } : g)),
    aggregates: def.aggregates || [],
    filters: def.filters || [],
    sort: def.sort || [],
    limit: def.limit || null,
    visualization: def.visualization || { type: 'table' },
  };
}

/**
 * Rows → chart series. With one measure and a second group-by column, the
 * measure is split into one series per value of that column (e.g. retention:
 * x = year, series = new / returning / lapsed). Mirrors server/reports/pdf.js.
 */
export function chartSeries(columns, rows, viz) {
  const y = (viz.y || []).filter((k) => columns.some((c) => c.key === k));
  const xKey = viz.x;
  const others = columns.filter((c) => c.key !== xKey && !y.includes(c.key) && !NUMERIC.includes(c.type));
  const pivot = others.length && y.length === 1 ? others[0].key : null;
  const byX = new Map();
  const point = (r) => {
    const x = r[xKey] === null || r[xKey] === undefined ? '—' : String(r[xKey]);
    if (!byX.has(x)) byX.set(x, { x });
    return byX.get(x);
  };
  let keys;
  if (pivot) {
    keys = [];
    for (const r of rows) {
      const name = r[pivot] === null || r[pivot] === undefined ? '—' : String(r[pivot]);
      if (!keys.includes(name)) keys.push(name);
      const p = point(r);
      p[name] = (p[name] || 0) + (Number(r[y[0]]) || 0);
    }
  } else {
    keys = y;
    for (const r of rows) {
      const p = point(r);
      for (const k of y) p[k] = (p[k] || 0) + (Number(r[k]) || 0);
    }
  }
  const labelOf = (k) => (pivot ? k : (columns.find((c) => c.key === k) || {}).label || k);
  return { data: [...byX.values()], series: keys.slice(0, 6).map((k) => ({ key: k, label: labelOf(k) })) };
}

/** Display a cell. */
export function formatCell(col, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (col.plain) return String(v);
  switch (col.type) {
    case 'boolean': return v ? 'Yes' : 'No';
    case 'currency': return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case 'percent': return `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    case 'integer': return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
    case 'number': return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
    default: return String(v);
  }
}
